// src/context/AuthContext.jsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { app, auth, db } from '@/firebase/firebase'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  getIdTokenResult,
  updateProfile,
} from 'firebase/auth'
import {
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  getDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore'
import Loader from '@/components/Loader'
import { toast } from 'react-hot-toast'
import {
  getMessaging,
  getToken,
  isSupported as isMessagingSupported,
} from 'firebase/messaging'

const AuthContext = createContext(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}

/* ------------------------------------------------------------------ */
/* Beta access allowlist                                              */
/* ------------------------------------------------------------------ */
// Scrimly is in private beta — only these emails can sign in or
// register. Everyone else (including pre-pivot legacy accounts) gets
// signed out + a friendly "we're not public yet" message.
//
// To open the gate: clear this set (or set BETA_GATE_ENABLED to false).
// To add a tester: drop their lowercased email into ALLOWED_EMAILS.
//
// This is a CLIENT-side gate — it's enough to stop normal users,
// but a determined attacker could bypass it by skipping the React
// app and calling Firebase Auth REST APIs directly. For real
// server-side enforcement, deploy a Firebase Auth blocking function
// (`beforeSignIn`) — that requires Identity Platform upgrade.
// Gate OPENED 2026-07-06 (founder decision): registration is public so the
// marketing CTAs ("create free account") are truthful and referred coaches
// stop hitting the wall. Flip back to true to re-gate; the allowlist below
// is kept for that case and is ignored while the gate is off.
export const BETA_GATE_ENABLED = false
export const ALLOWED_EMAILS = new Set([
  'kurt@getscrimly.com',
  'kurtryan@gmail.com',
  // ── Beta testers ─────────────────────────────────────────────
  'aphillips15121@icloud.com',     // Ariel Phillips — added 2026-05-04
  'tsfcpresident@gmail.com',       // Renae Walker, TSFC President — added 2026-05-05
  'dcarrillo711@gmail.com',        // Coach (referred by Kurt) — added 2026-06-22
  // ── Internal test accounts ───────────────────────────────────
  'hello@getscrimly.com',          // Kurt's fresh-signup test account — added 2026-05-06
])

export const BETA_GATE_MESSAGE =
  "Scrimly is in private beta and not open to the public yet. " +
  "If you'd like access, email hello@getscrimly.com."

export function isEmailAllowed(email) {
  if (!BETA_GATE_ENABLED) return true
  if (!email) return false
  return ALLOWED_EMAILS.has(String(email).toLowerCase().trim())
}

/* ------------------------------------------------------------------ */
/* Utils                                                              */
/* ------------------------------------------------------------------ */

const toE164 = (phone) => {
  if (!phone) return ''
  let cleaned = phone.trim().replace(/[^\d+]/g, '')
  if (cleaned.startsWith('+')) return cleaned
  if (cleaned.length === 10) return '+1' + cleaned
  if (cleaned.length === 11 && cleaned.startsWith('1')) return '+' + cleaned
  return ''
}

function hasTeamFields(data) {
  return (
    !!data?.teamId ||
    !!data?.currentTeam?.id ||
    (Array.isArray(data?.teams) && data.teams.length > 0)
  )
}

function isEffectivelyOnboarded(data, firebaseUserMaybe) {
  const emailOk = !!(
    firebaseUserMaybe?.emailVerified ||
    data?.emailVerified ||
    data?.trust?.emailVerified
  )
  const phoneOk = !!(data?.phoneVerified || data?.trust?.phoneVerified)
  const hasTeam = hasTeamFields(data)
  return emailOk && phoneOk && hasTeam
}

const normalizeOnboardingStatus = (data, firebaseUserMaybe) => {
  const raw =
    typeof data?.onboardingStatus === 'string' ? data.onboardingStatus.trim() : ''
  const rawLower = raw.toLowerCase()

  if (rawLower === 'completed' || rawLower === 'complete') return 'completed'
  if (data?.onboardingComplete === true) return 'completed'

  const flowStatuses = [
    'team_setup',
    'notifications_intro',
    'notifications_request',
    'notifications_configured',
    'freemium_intro'
  ];
  if (flowStatuses.includes(rawLower)) {
    return raw;
  }

  const hasTeam = hasTeamFields(data)
  const emailOk = !!(
    firebaseUserMaybe?.emailVerified ||
    data?.emailVerified ||
    data?.trust?.emailVerified
  )
  const phoneOk = !!(data?.phoneVerified || data?.trust?.phoneVerified)

  if (emailOk && phoneOk && hasTeam) return 'completed'
  if (hasTeam) return 'completed'

  if (raw) return raw
  return 'registered'
}

function looksLikeRegression(nextData, prevProfile, everOnboarded) {
  if (!prevProfile) return false

  const keys = nextData ? Object.keys(nextData) : []
  if (!nextData || keys.length === 0) return true

  const prevHadTeam = hasTeamFields(prevProfile)
  const nextHasTeam = hasTeamFields(nextData)

  const nextRawStatus =
    typeof nextData?.onboardingStatus === 'string'
      ? nextData.onboardingStatus.trim()
      : ''
  const nextHasStatus = !!nextRawStatus

  if (everOnboarded && !nextHasTeam && !nextHasStatus) return true
  if (prevHadTeam && !nextHasTeam && !nextHasStatus) return true

  return false
}

/* ------------------------------------------------------------------ */
/* Trust badge + user doc                                             */
/* ------------------------------------------------------------------ */

async function grantTrustBadge(uid, badge) {
  if (!uid || !badge) return
  try {
    const ref = doc(db, 'users', uid)
    const trustKey =
      badge === 'email_verified'
        ? 'emailVerified'
        : badge === 'phone_verified'
        ? 'phoneVerified'
        : 'contactVerified'

    await setDoc(
      ref,
      {
        updatedAt: serverTimestamp(),
        trustBadges: arrayUnion(badge),
        trust: { [trustKey]: true },
      },
      { merge: true }
    )

    if (badge === 'email_verified' || badge === 'phone_verified') {
      await updateDoc(ref, {
        [badge === 'email_verified' ? 'emailVerified' : 'phoneVerified']: true,
      })
    }
  } catch {}
}

async function ensureUserDoc(user) {
  if (!user?.uid) return
  try {
    const ref = doc(db, 'users', user.uid)
    const snap = await getDoc(ref)
    if (snap.exists()) return

    await setDoc(
      ref,
      {
        uid: user.uid,
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        onboardingStatus: 'registered',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
  } catch {}
}

async function touchPresence(uid) {
  if (!uid) return
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        presence: {
          lastSeenAt: serverTimestamp(),
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
  } catch {}
}

// Fields that change on every presence heartbeat / token refresh but never
// affect what we render. If a profile snapshot differs ONLY in these, we keep
// the previous object so the whole app doesn't re-render each time the window
// regains focus (presence write → snapshot → re-render = the dashboard glitch).
const VOLATILE_PROFILE_KEYS = ['presence', 'lastSeenAt', 'lastActive', 'updatedAt']
function profilesEquivalent(a, b) {
  if (!a || !b) return false
  const strip = (o) => {
    const c = { ...o }
    for (const k of VOLATILE_PROFILE_KEYS) delete c[k]
    return c
  }
  try {
    return JSON.stringify(strip(a)) === JSON.stringify(strip(b))
  } catch {
    return false
  }
}

// Fire-and-forget call to onUserSignIn. Refreshes `users/{uid}.lastActive`
// once per browser session so the Disengaged Parent smart action (see
// features/disengaged-parent/) has accurate signal. We dedup by uid in
// session storage to avoid hammering the callable on every token refresh
// (onAuthStateChanged also fires for silent token rotations).
const LAST_ACTIVE_SESSION_KEY = 'scrimly.lastActive.firedFor'
async function refreshLastActiveOnSignIn(user) {
  if (!user?.uid) return
  try {
    if (sessionStorage.getItem(LAST_ACTIVE_SESSION_KEY) === user.uid) return
  } catch {
    // sessionStorage unavailable (Safari private mode, etc.) — still
    // fire the call, just without de-dup. Worst case: one extra write
    // per token refresh, which is fine for a serverTimestamp() merge.
  }
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions')
    const callable = httpsCallable(getFunctions(app, 'us-central1'), 'onUserSignIn')
    await callable({})
    try { sessionStorage.setItem(LAST_ACTIVE_SESSION_KEY, user.uid) } catch {}
  } catch (err) {
    // Non-blocking — the feature degrades gracefully if this fails.
    // No PII in log per F5.
    // eslint-disable-next-line no-console
    console.warn('[AuthContext] onUserSignIn failed', err?.code || err?.message)
  }
}

async function saveFcmTokenForUser(user) {
  if (!user?.uid) return
  try {
    const supported = await isMessagingSupported().catch(() => false)
    if (!supported) return

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || ''
    if (!vapidKey) return

    if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
      return
    }

    const swReg =
      typeof navigator !== 'undefined' && navigator.serviceWorker
        ? await navigator.serviceWorker.ready.catch(() => null)
        : null

    const messaging = getMessaging(app)
    const token = await getToken(messaging, {
      vapidKey,
      ...(swReg ? { serviceWorkerRegistration: swReg } : {}),
    }).catch(() => null)

    if (token) {
      const tokenRef = doc(db, 'users', user.uid, 'fcmTokens', token)
      await setDoc(
        tokenRef,
        {
          token,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        },
        { merge: true }
      )
    }
  } catch {}
}

async function enablePushForUser(user) {
  if (!user?.uid) throw new Error('Missing user')
  const supported = await isMessagingSupported().catch(() => false)
  if (!supported) throw new Error('Push not supported on this device/browser.')
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || ''
  if (!vapidKey) throw new Error('Missing VAPID key.')
  if (typeof Notification === 'undefined') throw new Error('Notifications not supported.')
  const permission = await Notification.requestPermission().catch(() => 'denied')
  if (permission !== 'granted') throw new Error('Notification permission was not granted.')
  await saveFcmTokenForUser(user)
  return { success: true }
}

/* ------------------------------------------------------------------ */
/* Provider                                                           */
/* ------------------------------------------------------------------ */

export default function AuthProvider({ children }) {
  const [authLoading, setAuthLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(true)
  const [userProfile, setUserProfile] = useState(null)
  const [firebaseUser, setFirebaseUser] = useState(null)
  const [claims, setClaims] = useState(null)

  const hasHydratedProfileRef = useRef(false)
  const lastBeatRef = useRef(0)
  const healedOnboardingRef = useRef(new Set())
  const everOnboardedRef = useRef(false)

  // --- AUTO LOGOUT LOGIC (30 minutes idle, cross-tab aware) ---
  // 2026-07-24 rework: signOut() is browser-wide, but the old timer was
  // per-tab — one forgotten background tab hitting its 30-minute mark
  // kicked the coach out of the tab they were ACTIVELY using. Activity
  // is now stamped to localStorage (shared across all tabs) and each
  // tab checks that shared stamp on a heartbeat, so logout fires only
  // when the whole browser has been idle for the limit. mousemove now
  // counts too (reading a long message without clicking is activity).
  const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
  const LAST_ACTIVITY_KEY = 'scrimly.lastActivityAt';
  const ACTIVITY_STAMP_THROTTLE_MS = 15 * 1000; // storage write cap
  const IDLE_CHECK_EVERY_MS = 60 * 1000;
  const lastStampRef = useRef(0);

  const logout = useCallback(async (auto = false) => {
    try {
      await signOut(auth)
      localStorage.removeItem('scrimly.activeTeamId')
      if (auto) toast.error('Logged out due to inactivity.')
      window.location.href = '/login'
    } catch (error) {
      // silent fail
    }
  }, [])

  useEffect(() => {
    if (!firebaseUser?.uid) return;

    const stampActivity = () => {
      const now = Date.now();
      if (now - lastStampRef.current < ACTIVITY_STAMP_THROTTLE_MS) return;
      lastStampRef.current = now;
      try { localStorage.setItem(LAST_ACTIVITY_KEY, String(now)); } catch { /* storage unavailable */ }
    };

    // Events that count as "activity"
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(evt => window.addEventListener(evt, stampActivity, { passive: true }));

    // Logging in / mounting counts as activity, otherwise a stale stamp
    // from a previous session could log a fresh login straight out.
    lastStampRef.current = 0;
    stampActivity();

    const interval = setInterval(() => {
      let last = 0;
      try { last = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0; } catch { /* storage unavailable */ }
      if (!last) last = lastStampRef.current; // fallback: this tab's own stamp
      if (Date.now() - last >= INACTIVITY_LIMIT_MS) logout(true);
    }, IDLE_CHECK_EVERY_MS);

    return () => {
      clearInterval(interval);
      events.forEach(evt => window.removeEventListener(evt, stampActivity));
    };
  }, [firebaseUser?.uid, logout]);
  // --------------------------------------

  const refreshProfile = useCallback(async () => {
    if (!firebaseUser?.uid) return
    try {
      const ref = doc(db, 'users', firebaseUser.uid)
      const snap = await getDoc(ref)
      if (snap.exists()) {
        const data = snap.data() || {}
        setUserProfile((prev) => {
          if (looksLikeRegression(data, prev, everOnboardedRef.current)) return prev
          const normalized = normalizeOnboardingStatus(data, firebaseUser)
          if (String(normalized).toLowerCase() === 'completed') {
            everOnboardedRef.current = true
          }
          return {
            id: snap.id,
            ...data,
            emailVerified: firebaseUser.emailVerified || !!data.emailVerified || !!data?.trust?.emailVerified,
            phoneVerified: !!(data.phoneVerified || data?.trust?.phoneVerified),
            onboardingStatus: normalized,
            _everOnboarded: everOnboardedRef.current,
          }
        })
      } else {
        setUserProfile(null)
      }
    } catch (e) {}
  }, [firebaseUser])

  useEffect(() => {
    if (!firebaseUser?.uid) return
    if (profileLoading) return
    if (!userProfile?.id) return
    if (!hasHydratedProfileRef.current) return

    let alive = true
    const beat = async () => {
      if (!alive) return
      // Throttle focus/visibility-driven beats so rapid window switching
      // doesn't fire a presence write every time.
      const now = Date.now()
      if (now - lastBeatRef.current < 30_000) return
      lastBeatRef.current = now
      await touchPresence(firebaseUser.uid)
    }
    beat()
    const interval = setInterval(beat, 90_000)
    const onVis = () => { if (document.visibilityState === 'visible') beat() }

    window.addEventListener('focus', beat)
    document.addEventListener('visibilitychange', onVis)

    return () => {
      alive = false
      clearInterval(interval)
      window.removeEventListener('focus', beat)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [firebaseUser?.uid, profileLoading, userProfile?.id])

  useEffect(() => {
    let unsubProfile = null

    const maybeAutoHealOnboarding = async (uid, data, user) => {
      if (!uid) return
      if (healedOnboardingRef.current.has(uid)) return

      const status = data?.onboardingStatus || ''
      const inProgress = [
        'team_setup', 
        'notifications_intro', 
        'notifications_request', 
        'notifications_configured', 
        'freemium_intro'
      ]
      if (inProgress.includes(status)) return

      if (!isEffectivelyOnboarded(data, user)) return
      if (data?.onboardingComplete === true) return

      healedOnboardingRef.current.add(uid)

      try {
        await setDoc(
          doc(db, 'users', uid),
          {
            onboardingComplete: true,
            onboardingStatus: 'completed',
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      } catch (e) {}
    }

    const subscribeToProfile = (user) => {
      const ref = doc(db, 'users', user.uid)
      return onSnapshot(
        ref,
        (snap) => {
          if (!snap.exists()) {
            const creationTime = user.metadata?.creationTime
              ? new Date(user.metadata.creationTime).getTime()
              : 0
            const now = Date.now()
            const isBrandNew = now - creationTime < 15000

            if (isBrandNew) return

            setUserProfile(null)
            setProfileLoading(false)
            logout() // Use wrapper logout
            return
          }

          const data = snap.data() || {}
          const creationTime = user.metadata?.creationTime
            ? new Date(user.metadata.creationTime).getTime()
            : 0
          const isEstablished = Date.now() - creationTime > 10000

          const hasStatus = !!data.onboardingStatus
          const hasTeam = hasTeamFields(data)

          if (isEstablished && !hasStatus && !hasTeam) return

          setUserProfile((prev) => {
            if (looksLikeRegression(data, prev, everOnboardedRef.current)) return prev

            const normalized = normalizeOnboardingStatus(data, user)
            if (String(normalized).toLowerCase() === 'completed') {
              everOnboardedRef.current = true
            }

            const emailOk = user.emailVerified || !!data.emailVerified || !!data?.trust?.emailVerified
            const phoneOk = !!(data.phoneVerified || data?.trust?.phoneVerified)

            const next = {
              id: snap.id,
              ...data,
              emailVerified: emailOk,
              phoneVerified: phoneOk,
              onboardingStatus: normalized,
              _everOnboarded: everOnboardedRef.current,
            }

            // Presence heartbeat / token-refresh writes only touch volatile
            // fields. If nothing render-relevant changed, keep the SAME object
            // (no re-render, no auto-heal) so refocusing the window doesn't
            // glitch the dashboard.
            if (prev && profilesEquivalent(prev, next)) {
              if (!hasHydratedProfileRef.current) {
                hasHydratedProfileRef.current = true
                setProfileLoading(false)
              }
              return prev
            }

            maybeAutoHealOnboarding(user.uid, data, user)
            hasHydratedProfileRef.current = true
            setProfileLoading(false)
            return next
          })
        },
        (error) => {
          setUserProfile(null)
          setProfileLoading(false)
        }
      )
    }

    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      // ── Private beta gate ─────────────────────────────────────
      // If this user has a valid Firebase session but isn't on the
      // allowlist, sign them out immediately and bounce to /login
      // with a beta-closed flag. Catches three cases:
      //   1. Existing legacy users with active sessions
      //   2. Anyone who somehow re-authed without going through
      //      the login() path (e.g. magic link, Google OAuth)
      //   3. A leftover session from a previously allowed account
      //      that got removed from the allowlist
      // We set the flag in sessionStorage so /login can show a
      // persistent banner instead of just a transient toast.
      if (u && !isEmailAllowed(u.email)) {
        try {
          sessionStorage.setItem('scrimly.betaGate.blocked', '1')
        } catch {}
        setFirebaseUser(null)
        setUserProfile(null)
        setClaims(null)
        setAuthLoading(false)
        if (typeof unsubProfile === 'function') {
          unsubProfile()
          unsubProfile = null
        }
        await signOut(auth).catch(() => {})
        // Send them to login so they see the banner. Hard nav so
        // any in-flight protected-route renders are torn down.
        if (window.location.pathname !== '/login') {
          window.location.replace('/login')
        }
        return
      }

      setFirebaseUser(u ?? null)
      hasHydratedProfileRef.current = false
      everOnboardedRef.current = false

      if (u) {
        setProfileLoading(true)
        setUserProfile(null)

        if (typeof unsubProfile === 'function') unsubProfile()
        unsubProfile = subscribeToProfile(u)

        try {
          const res = await getIdTokenResult(u, true)
          setClaims(res.claims || null)
        } catch {}

        ensureUserDoc(u)
        saveFcmTokenForUser(u)
        // Fire-and-forget once per session. See refreshLastActiveOnSignIn.
        refreshLastActiveOnSignIn(u)
        if (u.emailVerified) grantTrustBadge(u.uid, 'email_verified')
        setAuthLoading(false)
      } else {
        if (typeof unsubProfile === 'function') {
          unsubProfile()
          unsubProfile = null
        }
        setClaims(null)
        setUserProfile(null)
        setProfileLoading(false)
        setAuthLoading(false)
      }
    })

    return () => {
      unsubAuth?.()
      unsubProfile?.()
    }
  }, [logout])

  const isOnboarded = useMemo(() => {
    if (!userProfile) return false
    const status = normalizeOnboardingStatus(userProfile, firebaseUser)
    return String(status).toLowerCase() === 'completed'
  }, [userProfile, firebaseUser])

  const login = async (email, password) => {
    // Beta gate — fail fast with a friendly error before we even
    // hit Firebase Auth, so non-allowlisted users don't see a
    // confusing "wrong password" / generic error if their email
    // happens to exist.
    if (!isEmailAllowed(email)) {
      const err = new Error(BETA_GATE_MESSAGE)
      err.code = 'auth/beta-closed'
      throw err
    }
    const cred = await signInWithEmailAndPassword(auth, email, password)
    saveFcmTokenForUser(cred.user)
    return cred
  }

  const register = async (email, password, firstName, lastName, selectedRole = 'user') => {
    // Beta gate — block all new account creation for non-allowlisted
    // emails. Even if the allowlist grows later, new users should
    // come in through an explicit invite flow, not open registration.
    if (!isEmailAllowed(email)) {
      const err = new Error(BETA_GATE_MESSAGE)
      err.code = 'auth/beta-closed'
      throw err
    }
    const cred = await createUserWithEmailAndPassword(auth, email, password)
    const u = cred.user
    try {
      if (firstName || lastName) {
        await updateProfile(u, {
          displayName: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
        })
      }
    } catch {}

    await setDoc(
      doc(db, 'users', u.uid),
      {
        uid: u.uid,
        email: u.email,
        firstName: firstName ?? null,
        lastName: lastName ?? null,
        displayName: `${firstName ?? ''} ${lastName ?? ''}`.trim() || null,
        createdAt: serverTimestamp(),
        emailVerified: false,
        phoneVerified: false,
        verificationStatus: 'pending_email',
        role: selectedRole,
        roles: [selectedRole],
        trustBadges: [],
        trust: {
          badges: { emailVerified: false, phoneVerified: false, contactVerified: false },
          emailVerified: false,
          phoneVerified: false,
          contactVerified: false,
        },
        onboardingStatus: 'registered',
        publicProfile: true,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
    saveFcmTokenForUser(u)
    return cred
  }

  const resetPassword = (email) => {
    toast.success('Password reset email sent.')
    return sendPasswordResetEmail(auth, email)
  }

  const patchUserProfile = async (patch) => {
    if (!firebaseUser?.uid) throw new Error('No user')
    await updateDoc(doc(db, 'users', firebaseUser.uid), {
      ...patch,
      updatedAt: serverTimestamp(),
    })
    setUserProfile((p) => (p ? { ...p, ...patch } : p))
  }

  const value = {
    firebaseUser,
    user: firebaseUser,
    userProfile,
    claims,
    isAdmin: !!(claims && claims.admin),
    loadingAuth: authLoading,
    profileLoading,
    isAuthenticated: !!firebaseUser,
    isOnboarded,
    everOnboarded: everOnboardedRef.current,
    login,
    register,
    logout: () => logout(false), // Manual logout
    resetPassword,
    updateUserProfile: patchUserProfile,
    updateUserProfileInFirestore: patchUserProfile,
    refreshProfile,
    grantTrustBadge: (badge) =>
      firebaseUser?.uid && grantTrustBadge(firebaseUser.uid, badge),
    saveFcmTokenForUser,
    enablePushForUser,
  }

  const showLoader = authLoading || (!!firebaseUser && profileLoading)
  if (showLoader) return <Loader />

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}